import { useEffect, useState } from "react";
import { getClinicianSocket } from "@/lib/clinicianSocket";
import { mocksEnabled } from "@/portals/clinician/api/transport";
import { AppModal } from "@/shared/components/dialog";
import { Button } from "@/shared/components/button";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { ClinicianReviewItem, QueueItemPreview } from "@/portals/clinician/api/reviewQueueApi";
import {
  clinicianKeys,
  forgetHeldItem,
  heldItemQuery,
  previewQuery,
  useClaimItem,
  useReleaseItem,
} from "@/portals/clinician/api/clinicianQueries";
import { AttachedViaTag, ReviewStatusBadge } from "./reviewBadges";
import { DecisionPanel } from "./DecisionPanel";
import { PatientAge, ReviewItemLayout, ReviewItemScreenMessage, ViewOnlyActionPanel } from "./ReviewItemLayout";
import { ReviewHistory } from "./ReviewHistory";

export const MESSAGES = {
  released: "Item released to the shared queue.",
  notReleasable: "This item can't be released right now.",
  notFound: "This item isn't available.",
  loadFailed: "Couldn't load this item. Please try again.",
  noAnswer: "No answer yet",
  claimedElsewhere: "Another clinician already claimed this item.",
  previewNote: "View only. Claim this item to record a decision or ask the patient a question.",
  closedByPatientTitle: "This item was closed",
  closedByPatient: "The system closed this item because the patient says they no longer take this medicine. There's nothing left to decide.",
};

type ScreenItem = ClinicianReviewItem | QueueItemPreview;

type ScreenState =
  | { kind: "loading" }
  | { kind: "ready"; item: ScreenItem }
  | { kind: "not-found" }
  | { kind: "claimed-elsewhere" }
  | { kind: "load-failed" };

const isHeld = (item: ScreenItem): item is ClinicianReviewItem => "attachedVia" in item;

interface ClinicianReviewItemViewProps {
  itemId: string;
  /** Where Back goes, for its label ("My Items" / "Review Queue"). */
  backLabel?: string;
  onBack: () => void;
  /** After a successful release — back to the dashboard. */
  onReleased: () => void;
  /** After a resolving decision — back to the dashboard (the item leaves My items). */
  onDecided?: () => void;
  /** "preview": a shared-queue item looked at before claiming — read-only,
   *  no patient identity, a Claim button in place of the decision panel. */
  mode?: "held" | "preview";
  /** Preview only: after a successful claim (the item is now held). */
  onClaimed?: (id: string) => void;
}

export function ClinicianReviewItemView({ itemId, backLabel = "Review Queue", onBack, onReleased, onDecided, mode = "held", onClaimed }: ClinicianReviewItemViewProps) {
  const preview = mode === "preview";
  const [afterAction, setAfterAction] = useState<"not-found" | "claimed-elsewhere" | null>(null);
  // The ONE modal of the review flow: the patient answered "I no longer take
  // this" while this clinician had the item open, so the system closed it.
  const [closedByPatient, setClosedByPatient] = useState(false);
  useEffect(() => {
    if (preview || mocksEnabled()) return;
    const socket = getClinicianSocket();
    const onNote = (n: { type?: string; data?: { reviewItemId?: string } | null }) => {
      if (n?.type === "REVIEW_ITEM_CLOSED_BY_PATIENT" && n.data?.reviewItemId === itemId) setClosedByPatient(true);
    };
    socket.on("notification:new", onNote);
    return () => {
      socket.off("notification:new", onNote);
    };
  }, [itemId, preview]);

  // Cached per item: reopening an item shows it at once, then revalidates
  // quietly. Live: a patient answering (or resolving it with "I no longer
  // take this") arrives as a notification / queue event, which refetches it
  // (see useClinicianLiveQueries).
  const client = useQueryClient();
  const heldQuery = useQuery({ ...heldItemQuery(itemId), enabled: !preview });
  const previewItemQuery = useQuery({ ...previewQuery(itemId), enabled: preview });
  const query = (preview ? previewItemQuery : heldQuery) as UseQueryResult<ScreenItem>;
  const load = () => void query.refetch();
  const queryError = query.error as { code?: string } | null;
  const state: ScreenState = afterAction
    ? { kind: afterAction }
    : query.data
      ? { kind: "ready", item: query.data }
      : !query.isError
        ? { kind: "loading" }
        : {
            kind: queryError?.code === "REVIEW_ITEM_NOT_FOUND"
              ? "not-found"
              : queryError?.code === "ITEM_ALREADY_CLAIMED"
                ? "claimed-elsewhere"
                : "load-failed",
          };
  const setState = (next: { kind: "ready"; item: ClinicianReviewItem } | { kind: "not-found" }) => {
    if (next.kind === "ready") client.setQueryData(clinicianKeys.item(itemId), next.item);
    else setAfterAction("not-found");
  };
  // After a release or a resolving decision the item has left My items — drop it
  // there right away (no stale row when the dashboard reappears) and forget this
  // item's cached copy, so it's reloaded fresh if opened again.
  const forgetItem = () => forgetHeldItem(client, itemId);

  const item = state.kind === "ready" ? state.item : null;

  // Preview → claim: on success the item is held — its full view is seeded in
  // the cache (opens instantly) and it leaves the shared queue.
  const claimMutation = useClaimItem();
  const claiming = claimMutation.isPending;
  const claim = () => {
    claimMutation.mutate(itemId, {
      onSuccess: () => onClaimed?.(itemId),
      onError: (caught) => {
        const error = caught as { code?: string; message?: string };
        if (error.code === "ITEM_ALREADY_CLAIMED") {
          toastError(MESSAGES.claimedElsewhere);
          setAfterAction("claimed-elsewhere");
        } else if (error.code === "REVIEW_ITEM_NOT_FOUND") {
          setAfterAction("not-found");
        } else {
          toastError(error.message ?? "Unable to claim this item.");
        }
      },
    });
  };

  const releaseMutation = useReleaseItem();
  const releasing = releaseMutation.isPending;
  const release = () => {
    if (!item) return;
    releaseMutation.mutate(item.id, {
      onSuccess: () => {
        toastSuccess(MESSAGES.released);
        onReleased();
      },
      onError: (caught) => {
        const error = caught as { code?: string; message?: string };
        if (error.code === "ITEM_NOT_RELEASABLE") {
          toastError(MESSAGES.notReleasable);
          load();
        } else if (error.code === "REVIEW_ITEM_NOT_FOUND") {
          setState({ kind: "not-found" });
        } else {
          toastError(error.message ?? "Unable to release this item.");
        }
      },
    });
  };

  const closedModal = (
    <AppModal
      open={closedByPatient}
      onOpenChange={(open) => {
        if (!open) {
          setClosedByPatient(false);
          forgetItem();
          onBack();
        }
      }}
      title={<span className="text-[#0A2947]">{MESSAGES.closedByPatientTitle}</span>}
      description={<span className="text-[#4B5563]">{MESSAGES.closedByPatient}</span>}
      width={440}
      footer={
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setClosedByPatient(false);
              forgetItem();
              onBack();
            }}
            className="!h-10 !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-6 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white"
          >
            OK
          </Button>
        </div>
      }
    />
  );
  const lastItem = query.data;

  if (closedByPatient && lastItem) {
    return (
      <>
        <ReviewItemLayout
          item={lastItem}
          backLabel={backLabel}
          onBack={onBack}
          identity={<PatientAge age={lastItem.patientAge} />}
          badges={<ReviewStatusBadge status={lastItem.itemStatus} />}
          renderConversation={(isPair) => <ReviewHistory item={lastItem} isPair={isPair} readOnly />}
          aside={null}
        />
        {closedModal}
      </>
    );
  }

  if (state.kind !== "ready") {
    return (
      <ReviewItemScreenMessage
        loading={state.kind === "loading"}
        icon={state.kind === "not-found" ? "lucide:file-x" : state.kind === "claimed-elsewhere" ? "lucide:user-check" : "lucide:wifi-off"}
        message={state.kind === "not-found" ? MESSAGES.notFound : state.kind === "claimed-elsewhere" ? MESSAGES.claimedElsewhere : MESSAGES.loadFailed}
        backLabel={backLabel}
        onBack={onBack}
      />
    );
  }

  const it = state.item;
  // IDENTITY_MISMATCH is patient-only: the backend never serves one here, and
  // this screen never offers a clinician path for one either.
  if (it.triggerType === "IDENTITY_MISMATCH") {
    return <ReviewItemScreenMessage icon="lucide:file-x" message={MESSAGES.notFound} backLabel={backLabel} onBack={onBack} />;
  }

  return (
    <ReviewItemLayout
      item={it}
      backLabel={backLabel}
      onBack={onBack}
      identity={<PatientAge age={it.patientAge} />}
      badges={
        <>
          <ReviewStatusBadge status={it.itemStatus} />
          {isHeld(it) && <AttachedViaTag via={it.attachedVia} />}
        </>
      }
      // Patient Conversation: questions, answers and the decision, in order — on the preview too (read-only).
      renderConversation={(isPair) => <ReviewHistory item={it} isPair={isPair} readOnly={!isHeld(it)} />}
      aside={
        // Decision panel (Module 5) + release — or, in preview, only Claim.
        !isHeld(it) ? (
          <ViewOnlyActionPanel
            testId="preview-claim-panel"
            note={MESSAGES.previewNote}
            actionLabel="Claim this item"
            busy={claiming}
            onAction={claim}
          />
        ) : (
          <DecisionPanel
            key={`${it.id}:${it.itemStatus}`}
            item={it}
            onResolved={() => {
              forgetItem();
              (onDecided ?? onBack)();
            }}
            onItemChanged={(updated) => (updated ? setState({ kind: "ready", item: updated }) : load())}
            onNotFound={() => setState({ kind: "not-found" })}
            // Same line as "Record decision", inside the panel.
            secondaryAction={it.itemStatus === "IN_CLINICIAN_REVIEW" || it.itemStatus === "NEEDS_MORE_INFO" ? (
              <Button
                variant="outline"
                busy={releasing}
                onClick={release}
                className="!h-10 !w-full !rounded-lg !border-[#E5E7EB] !text-sm !font-semibold !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]"
              >
                Release to queue
              </Button>
            ) : undefined}
          />
        )
      }
    />
  );
}
